import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Badge, Button, Card, DL, Dialog, ErrorBanner, FilterSelect, PageHeader, Pagination, SearchBox, Spinner, Table, Textarea } from '../../components/ui';
import { api, download } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { date, dateTime, inr } from '../../lib/format';
import { useAction, useUrlState, type Paged } from '../../lib/hooks';

interface Row { id: string; name: string | null; email: string | null; phone: string | null; status: string; joinedAt: string; orders: number; spentPaise: number; lastOrderAt: string | null }
const DEFAULTS = { q: '', status: '', page: '1' };

export function Customers() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [f, setF] = useUrlState(DEFAULTS);
  const query = { ...f, pageSize: 25 };
  const q = useQuery({ queryKey: ['customers', query], queryFn: () => api<Paged<Row>>('/admin/customers', { query }), placeholderData: keepPreviousData });
  return (
    <>
      <PageHeader title="Customers" actions={can('customers.export') && <Button variant="secondary" onClick={() => void download('/admin/customers/export', { q: f.q, status: f.status })}><Download className="h-4 w-4" />Export CSV</Button>} />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} placeholder="Name, email or phone" />
          <FilterSelect label="Status" value={f.status} onChange={(v) => setF({ status: v })} options={[{ value: '', label: 'All' }, { value: 'ACTIVE', label: 'Active' }, { value: 'DISABLED', label: 'Disabled' }, { value: 'DELETED', label: 'Deleted' }]} />
        </div>
        {q.isLoading ? <Spinner /> : q.error ? <ErrorBanner error={q.error} /> : (
          <>
            <Table rows={q.data!.items} rowKey={(c) => c.id} onRowClick={(c) => nav(`/customers/${c.id}`)} empty="No customers found." columns={[
              { header: 'Customer', cell: (c) => <div><div className="font-medium text-forest-800">{c.name ?? '—'}</div><div className="text-xs text-ink-muted">{c.email ?? c.phone}</div></div> },
              { header: 'Joined', cell: (c) => date(c.joinedAt) },
              { header: 'Orders', cell: (c) => c.orders, className: 'text-right' },
              { header: 'Net spent', cell: (c) => inr(c.spentPaise), className: 'text-right' },
              { header: 'Last order', cell: (c) => date(c.lastOrderAt) },
              { header: 'Status', cell: (c) => <Badge status={c.status} /> },
            ]} />
            <Pagination page={q.data!.page} totalPages={q.data!.totalPages} total={q.data!.total} onPage={(p) => setF({ page: String(p) } as never)} />
          </>
        )}
      </Card>
    </>
  );
}

interface Detail {
  id: string; email: string | null; phone: string | null; status: string; createdAt: string; lastLoginAt: string | null; piiMasked: boolean;
  customerProfile: { fullName: string; referralCode: string | null; marketingOptIn: boolean } | null;
  addresses: { id: string; label?: string | null; fullName: string; line1: string; line2?: string | null; city: string; state: string; pincode: string; phone: string; isDefault?: boolean }[];
  stats: { orders: number; spentPaise: number; lastOrderAt: string | null };
  orders: { id: string; orderNumber: string; status: string; paymentStatus: string; paymentMethod: string; totalPaise: number; placedAt: string }[];
}

export function CustomerDetail() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['customer', id], queryFn: () => api<Detail>(`/admin/customers/${id}`) });
  const [dialog, setDialog] = useState<null | 'status' | 'erase'>(null);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBanner error={q.error} />;
  const c = q.data!;
  return (
    <>
      <Link to="/customers" className="mb-2 inline-flex items-center gap-1 text-sm text-forest-800 hover:underline"><ArrowLeft className="h-4 w-4" />Customers</Link>
      <PageHeader title={c.customerProfile?.fullName ?? 'Customer'} subtitle={`Customer since ${date(c.createdAt)}`} actions={<>
        <Badge status={c.status} />
        {can('customers.update') && c.status !== 'DELETED' && <Button variant="secondary" onClick={() => setDialog('status')}>{c.status === 'ACTIVE' ? 'Disable account' : 'Re-enable account'}</Button>}
        {can('customers.delete') && c.status !== 'DELETED' && <Button variant="danger" onClick={() => setDialog('erase')}>Erase personal data</Button>}
      </>} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <Card title="Profile">
            {c.piiMasked && <p className="mb-2 text-xs text-ink-muted">Contact details are hidden for your role.</p>}
            <DL items={[['Email', c.email], ['Phone', c.phone], ['Last sign-in', dateTime(c.lastLoginAt)], ['Marketing', c.customerProfile?.marketingOptIn ? 'Opted in' : 'No'], ['Orders', c.stats.orders], ['Net spent', inr(c.stats.spentPaise)]]} />
          </Card>
          <Card title="Addresses">
            {c.addresses.length ? <ul className="space-y-3 text-sm">{c.addresses.map((a) => <li key={a.id}><p className="font-medium">{a.fullName}{a.isDefault && <span className="ml-1 text-xs text-ink-muted">(default)</span>}</p><p className="text-ink-muted">{[a.line1, a.line2, `${a.city}, ${a.state} ${a.pincode}`, a.phone].filter(Boolean).join(' · ')}</p></li>)}</ul> : <p className="text-sm text-ink-muted">None saved.</p>}
          </Card>
        </div>
        <Card title="Orders" className="lg:col-span-2">
          <Table rows={c.orders} rowKey={(o) => o.id} empty="No orders yet." columns={[
            { header: 'Order', cell: (o) => <Link className="font-medium text-forest-800 hover:underline" to={`/orders/${o.id}`}>{o.orderNumber}</Link> },
            { header: 'Placed', cell: (o) => dateTime(o.placedAt) },
            { header: 'Status', cell: (o) => <Badge status={o.status} /> },
            { header: 'Payment', cell: (o) => <Badge status={o.paymentStatus} /> },
            { header: 'Total', cell: (o) => inr(o.totalPaise), className: 'text-right' },
          ]} />
        </Card>
      </div>
      {dialog === 'status' && <StatusDialog c={c} onClose={() => setDialog(null)} />}
      {dialog === 'erase' && <EraseDialog c={c} onClose={() => setDialog(null)} />}
    </>
  );
}

function StatusDialog({ c, onClose }: { c: Detail; onClose: () => void }) {
  const next = c.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
  const [reason, setReason] = useState('');
  const m = useAction(() => api(`/admin/customers/${c.id}/status`, { method: 'PATCH', body: { status: next, reason } }), { success: next === 'DISABLED' ? 'Account disabled and signed out everywhere' : 'Account re-enabled', invalidate: [['customer', c.id], ['customers']], onSuccess: onClose });
  return (
    <Dialog open title={next === 'DISABLED' ? 'Disable account' : 'Re-enable account'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant={next === 'DISABLED' ? 'danger' : 'primary'} loading={m.isPending} disabled={reason.trim().length < 3} onClick={() => m.mutate(undefined)}>Confirm</Button></>}>
      {next === 'DISABLED' && <p className="text-sm">The customer is signed out on all devices and can't sign in or place orders until re-enabled.</p>}
      <Textarea label="Reason (kept in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
    </Dialog>
  );
}

function EraseDialog({ c, onClose }: { c: Detail; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const m = useAction(() => api(`/admin/customers/${c.id}/anonymize`, { method: 'POST', body: { confirm: true, reason } }), { success: 'Personal data erased', invalidate: [['customer', c.id], ['customers']], onSuccess: onClose });
  return (
    <Dialog open title="Erase personal data" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant="danger" loading={m.isPending} disabled={reason.trim().length < 3 || typed !== 'ERASE'} onClick={() => m.mutate(undefined)}>Erase permanently</Button></>}>
      <p className="text-sm">For a customer's data-deletion request. Name, email, phone, addresses, cart, wishlist and devices are erased and the account is closed. <strong>This cannot be undone.</strong> Orders and invoices are kept because tax law requires them. Not possible while orders are still open.</p>
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
      <label className="block text-sm">Type <strong>ERASE</strong> to confirm<input aria-label="Type ERASE to confirm" className="mt-1 w-full rounded-md border border-cream-border px-3 py-2" value={typed} onChange={(e) => setTyped(e.target.value)} /></label>
    </Dialog>
  );
}
